import React, { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext();

const translations = {
  de: {
    // Navigation
    miteinander: 'Miteinander',
    marktplatz: 'Marktplatz',
    forum: 'Forum',
    wissen: 'Wissen & Blog',
    freunde: 'Freunde',
    chat: 'Chat',
    location: 'Standort',
    profile: 'Profil',
    settings: 'Einstellungen',
    security: 'Sicherheit',
    logout: 'Abmelden',
    feed: 'Miteinander',
    marketplace: 'Marktplatz',
    blog: 'Wissen & Blog',
    friends: 'Freunde',
    
    // Common
    save: 'Speichern',
    cancel: 'Abbrechen',
    delete: 'Löschen',
    loading: 'Laden...',
    success: 'Erfolgreich',
    error: 'Fehler',
    
    // Dashboard / Feed
    feed_title: 'Miteinander',
    feed_subtitle: 'Finde Hilfe und biete deine Unterstützung in der Nachbarschaft an',
    new_post: 'Ersten Post erstellen',
    all_categories: 'Alle',
    help_sought: 'Hilfe gesucht',
    help_offered: 'Hilfe angeboten',
    events: 'Events',
    general: 'Allgemein',
    
    // Marketplace
    marketplace_title: 'Marktplatz',
    marketplace_subtitle: 'Teilen, leihen, schenken und tauschen mit deinen Nachbarn',
    
    // Chat
    chat_title: 'Chat',
    chat_subtitle: 'Kommuniziere direkt mit deinen Nachbarn',
    no_chats: 'Noch keine Chats',
    no_chats_desc: 'Schreibe deinen Nachbarn!',
    select_chat: 'Wähle eine Unterhaltung',
    select_chat_desc: 'Klicke links auf einen Chat, um zu schreiben',
    
    // Settings
    settings_title: 'Einstellungen',
    settings_subtitle: 'Konfiguriere deine App-Einstellungen',
    notifications: 'Benachrichtigungen',
    app_settings: 'App-Einstellungen',
    privacy: 'Privatsphäre'
  },
  en: {
    // Navigation
    miteinander: 'Feed',
    marktplatz: 'Marketplace',
    forum: 'Forum',
    wissen: 'Blog & Knowledge',
    freunde: 'Friends',
    chat: 'Chat',
    location: 'Location',
    profile: 'Profile',
    settings: 'Settings',
    security: 'Security',
    logout: 'Logout',
    feed: 'Feed',
    marketplace: 'Marketplace',
    blog: 'Blog & Knowledge',
    friends: 'Friends',
    
    // Common
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    loading: 'Loading...',
    success: 'Success',
    error: 'Error',
    
    // Dashboard / Feed
    feed_title: 'Community Feed',
    feed_subtitle: 'Find help and offer support in your neighborhood',
    new_post: 'Create your first post',
    all_categories: 'All',
    help_sought: 'Help Wanted',
    help_offered: 'Help Offered',
    events: 'Events',
    general: 'General',
    
    // Marketplace
    marketplace_title: 'Marketplace',
    marketplace_subtitle: 'Share, lend, gift and swap with your neighbors',
    
    // Chat
    chat_title: 'Chat',
    chat_subtitle: 'Communicate directly with your neighbors',
    no_chats: 'No chats yet',
    no_chats_desc: 'Send a message to your neighbors!',
    select_chat: 'Select a conversation',
    select_chat_desc: 'Click on a chat on the left to start messaging',
    
    // Settings
    settings_title: 'Settings',
    settings_subtitle: 'Configure your app settings',
    notifications: 'Notifications',
    app_settings: 'App Settings',
    privacy: 'Privacy'
  },
  fr: {
    // Navigation
    miteinander: 'Entraide',
    marktplatz: 'Marché',
    forum: 'Forum',
    wissen: 'Blog & Savoir',
    freunde: 'Amis',
    chat: 'Chat',
    location: 'Localisation',
    profile: 'Profil',
    settings: 'Paramètres',
    security: 'Sécurité',
    logout: 'Déconnexion',
    feed: 'Entraide',
    marketplace: 'Marché',
    blog: 'Blog & Savoir',
    friends: 'Amis',
    
    // Common
    save: 'Enregistrer',
    cancel: 'Annuler',
    delete: 'Supprimer',
    loading: 'Chargement...',
    success: 'Succès',
    error: 'Erreur',
    
    // Dashboard / Feed
    feed_title: 'Fil d\'entraide',
    feed_subtitle: 'Trouvez de l\'aide et proposez votre soutien dans le quartier',
    new_post: 'Créer votre premier post',
    all_categories: 'Tous',
    help_sought: 'Demande d\'aide',
    help_offered: 'Aide proposée',
    events: 'Événements',
    general: 'Général',
    
    // Marketplace
    marketplace_title: 'Marché de quartier',
    marketplace_subtitle: 'Partager, prêter, offrir et échanger avec vos voisins',
    
    // Chat
    chat_title: 'Chat',
    chat_subtitle: 'Communiquez directement avec vos voisins',
    no_chats: 'Pas encore de chat',
    no_chats_desc: 'Écrivez à vos voisins !',
    select_chat: 'Choisissez une conversation',
    select_chat_desc: 'Cliquez sur un chat à gauche pour commencer à écrire',
    
    // Settings
    settings_title: 'Paramètres',
    settings_subtitle: 'Configurez vos paramètres d\'application',
    notifications: 'Notifications',
    app_settings: 'Paramètres de l\'App',
    privacy: 'Confidentialité'
  }
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState('de');

  // Load language from settings saved in localStorage
  useEffect(() => {
    const updateLanguage = () => {
      try {
        const saved = localStorage.getItem('helpbook_settings');
        if (saved) {
          const parsed = JSON.parse(saved);
          if (parsed.app && parsed.app.language) {
            setLanguage(parsed.app.language);
          }
        }
      } catch (e) {
        console.error('Failed to load saved language:', e);
      }
    };

    updateLanguage();

    // Set up a listener for storage changes so language updates instantly if changed in another window/tab
    window.addEventListener('storage', updateLanguage);
    
    // Set up a custom interval to check for changes within the same tab, since 'storage' event doesn't fire inside the same window
    const interval = setInterval(updateLanguage, 500);
    
    return () => {
      window.removeEventListener('storage', updateLanguage);
      clearInterval(interval);
    };
  }, []);

  const changeLanguage = (lang) => {
    if (translations[lang]) {
      setLanguage(lang);
      
      // Also update localStorage settings
      try {
        const saved = localStorage.getItem('helpbook_settings');
        let currentSettings = { app: { language: lang } };
        if (saved) {
          currentSettings = JSON.parse(saved);
          currentSettings.app = { ...currentSettings.app, language: lang };
        }
        localStorage.setItem('helpbook_settings', JSON.stringify(currentSettings));
      } catch (e) {
        console.error('Failed to save language setting:', e);
      }
    }
  };

  const t = (key) => {
    return translations[language][key] || translations['de'][key] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, changeLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useTranslation = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useTranslation must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;
